import { isAxiosError } from 'axios';

export function normalizeError(value: unknown): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

/** Plain fields for logging and reporters — `Error` serializes to `{}` in JSON / some DevTools views. */
export function serializeError(err: Error): Record<string, string> {
  return {
    name: err.name,
    message: err.message,
    stack: err.stack ?? '',
  };
}

/**
 * Chunk / dev-module load failures (offline, sleep, Vite). Browsers and Vite often keep a failed
 * module record, so in-app "retry" with new React.lazy() may not refetch; a full reload is reliable.
 */
export function isLikelyDynamicImportFailure(message: string): boolean {
  if (!message) return false;
  const m = message.toLowerCase();
  return (
    m.includes('failed to fetch dynamically imported module') ||
    m.includes('loading chunk') ||
    m.includes('failed to load module script') ||
    m.includes('importing a module script failed') ||
    (m.includes('failed to fetch') && m.includes('module'))
  );
}

export type ErrorReportContext = Record<string, unknown>;

type ErrorReporter = (error: Error, context?: ErrorReportContext) => void;

const reporters: ErrorReporter[] = [];

export function registerErrorReporter(reporter: ErrorReporter): () => void {
  reporters.push(reporter);
  return () => {
    const i = reporters.indexOf(reporter);
    if (i !== -1) reporters.splice(i, 1);
  };
}

/** Context for API failure logs (used with `wrapApiCall` swallow pattern; `logApiError` only prints in dev). */
export type LogApiErrorInfo = {
  /** Human label from the service (typically the second argument to `wrapApiCall`) */
  defaultMessage: string;
  status: number;
  message: string;
  details?: unknown;
};

/**
 * Logs swallowed HTTP/API failures in local dev only (`import.meta.env.DEV`).
 * Does not run production reporters — use `reportError` when an error should surface in Sentry etc.
 */
export function logApiError(caughtError: unknown, info: LogApiErrorInfo): void {
  if (!import.meta.env.DEV) return;

  let url: string | undefined;
  let method: string | undefined;
  if (isAxiosError(caughtError)) {
    const cfg = caughtError.config;
    method = cfg?.method?.toUpperCase();
    const base = (cfg?.baseURL ?? '').replace(/\/$/, '');
    const path = (cfg?.url ?? '').replace(/^\//, '');
    url = base && path ? `${base}/${path}` : path || base || undefined;
  }

  console.warn(`[app:api] ${info.status || 'network'} ${info.message}`, {
    operation: info.defaultMessage,
    url,
    method,
    details: info.details,
  });
}

export function reportError(error: unknown, context?: ErrorReportContext): void {
  const err = normalizeError(error);
  const serialized = serializeError(err);
  const payload: ErrorReportContext = { ...serialized, ...context };
  console.error('[app:error]', serialized.message, payload);
  for (const reporter of reporters) {
    try {
      reporter(err, payload);
    } catch (e) {
      console.error('[app:error] reporter failed', e);
    }
  }
}

let globalHandlersInstalled = false;

export function initGlobalErrorHandlers(): void {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, {
      source: 'window',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}
